import { buildSearchParamsString } from '@/helpers/searchHospitalListingsHelper';
import {
  api_portalOfficeHospitalListing_medicalSubject,
  api_portalOfficeHospitalListing_reportedAdditionalFee,
  api_portalOfficeHospitalListing_responseFilter,
} from '@/init/swagger';
import { SearchHospitalsType } from '@/schemas/SearchParamsSchemas/app/stations/search/hospitals/Schema';
import { HOSPITAL_SEARCH_FILTER } from '@/types/types';
import { useRouter } from 'next/navigation';
import { useTransition } from 'react';
import { useFormContext } from 'react-hook-form';

type Props = {
  hospitalMedicalSubjects: api_portalOfficeHospitalListing_medicalSubject[];
  hospitalReportedAdditionalFees: api_portalOfficeHospitalListing_reportedAdditionalFee[];
  hospitalMedicalCares: api_portalOfficeHospitalListing_responseFilter[];
  hospitalAppealPoints: api_portalOfficeHospitalListing_responseFilter[];
  isPublicSearch: boolean;
};

export const useLogic = ({
  hospitalMedicalSubjects,
  hospitalReportedAdditionalFees,
  hospitalMedicalCares,
  hospitalAppealPoints,
  isPublicSearch,
}: Props) => {
  const router = useRouter();
  const { getValues, watch, setValue } = useFormContext<SearchHospitalsType>();
  const [isPending, startTransition] = useTransition();

  const search = async () => {
    setValue('page', 1);
    const formValues = getValues();
    const searchParams = buildSearchParamsString({ formValues });
    startTransition(() => {
      router.push(
        `/${isPublicSearch ? 'public' : 'app'}/stations/search?${searchParams}`,
      );
    });
  };

  const medicalSubjectSelectedFilterLabels = [
    ...watch('medical_subjects')?.map(
      (medicalSubjectId) =>
        hospitalMedicalSubjects.find(
          (medicalSubject) => medicalSubject.id === medicalSubjectId,
        )?.label,
    ),
    ...watch('reported_additional_fees')?.map(
      (reportedAdditionalFeeId) =>
        hospitalReportedAdditionalFees.find(
          (reportedAdditionalFee) =>
            reportedAdditionalFee.id === reportedAdditionalFeeId,
        )?.label,
    ),
  ].join('、');

  const medicalCareSelectedFilterLabels =
    watch('filters')
      ?.filter(
        (v) =>
          v.category_id === HOSPITAL_SEARCH_FILTER.MEDICAL_CARE_CATEGORY_ID,
      )
      .map(
        ({ search_filter_id }) =>
          hospitalMedicalCares.find(
            (medicalCare) => medicalCare.id === search_filter_id,
          )?.label,
      )
      .join('、') ?? '';

  const appealPointSelectedFilterLabels =
    watch('filters')
      ?.filter(
        (v) =>
          v.category_id === HOSPITAL_SEARCH_FILTER.APPEAL_POINT_CATEGORY_ID,
      )
      .map(
        ({ search_filter_id }) =>
          hospitalAppealPoints.find(
            (appealPoint) => appealPoint.id === search_filter_id,
          )?.label,
      )
      .join('、') ?? '';

  return {
    isPending,
    search,
    watch,
    medicalSubjectSelectedFilterLabels,
    medicalCareSelectedFilterLabels,
    appealPointSelectedFilterLabels,
  };
};
